// Label operations — create / delete / set-active / update / cancel.
//
// Labels are track-context tags (corner numbers, area names) used by
// the runtime HUD/announcer system, not rendered in the simulator.
// Two kinds:
//   { kind: 'corner', text, segmentIdx, t }
//   { kind: 'area',   text, startSegIdx, startT, endSegIdx, endT }
//
// Authoring flow lives in main.js: a "pendingLabel" sub-state on the
// editor state captures clicks while the user is dropping a corner or
// the start/end pair of an area.

import { pushHistory } from './history.js';

// Next sequential corner number, used as the default text for a new
// corner so the common case is "click click click" rather than
// "click — type — click — type". Picks the smallest positive integer
// not currently in use as a corner text. Authors who want "5a" or
// "T1" just edit the text after placement.
function nextCornerText(labels) {
  const used = new Set();
  for (const L of labels) {
    if (L.kind !== 'corner') continue;
    const n = parseInt(L.text, 10);
    if (Number.isInteger(n) && n > 0 && String(n) === String(L.text).trim()) {
      used.add(n);
    }
  }
  for (let n = 1; n <= used.size + 1; n++) {
    if (!used.has(n)) return String(n);
  }
  return '1';
}

// Drop a corner label at (segIdx, t). Auto-text from the next free
// integer; the author can edit it in the right-side selection panel.
export function placeCornerLabel(state, segIdx, t, notify) {
  const td = state.trackData;
  if (!Array.isArray(td.labels)) td.labels = [];
  const label = {
    kind: 'corner',
    text: nextCornerText(td.labels),
    segmentIdx: segIdx,
    t,
  };
  td.labels.push(label);
  state.activeLabelIdx = td.labels.length - 1;
  state.pendingLabel = null;
  pushHistory(state, notify);
}

// Two-click area placement. The first click stashes the start on
// `pendingLabel`; the second click calls this to commit the full
// label with a default name ("New area"). The author renames it from
// the right-side selection panel.
export function placeAreaLabel(state, startSegIdx, startT, endSegIdx, endT, notify) {
  const td = state.trackData;
  if (!Array.isArray(td.labels)) td.labels = [];
  const label = {
    kind: 'area',
    text: 'New area',
    startSegIdx,
    startT,
    endSegIdx,
    endT,
  };
  td.labels.push(label);
  state.activeLabelIdx = td.labels.length - 1;
  state.pendingLabel = null;
  pushHistory(state, notify);
}

export function deleteLabel(state, idx, notify) {
  const labels = state.trackData.labels;
  if (!labels || idx < 0 || idx >= labels.length) return;
  labels.splice(idx, 1);
  if (state.activeLabelIdx === idx)        state.activeLabelIdx = -1;
  else if (state.activeLabelIdx > idx)     state.activeLabelIdx -= 1;
  pushHistory(state, notify);
}

export function setActiveLabel(state, idx, notify) {
  if (idx === state.activeLabelIdx) return;
  state.activeLabelIdx = idx;
  state.pendingLabel = null;       // selecting cancels any in-progress placement
  notify();
}

// Patch the active label (text). Caller chooses live vs commit history
// via the editor's applyChange / applyChangeLive pattern.
export function updateActiveLabel(state, patch) {
  const L = state.trackData.labels?.[state.activeLabelIdx];
  if (!L) return;
  Object.assign(L, patch);
}

// Begin placement of a new corner. Next centerline click will commit.
export function beginPlaceCorner(state, notify) {
  state.pendingLabel = { kind: 'corner' };
  state.activeLabelIdx = -1;
  notify();
}

// Begin placement of a new area. First click commits the start onto
// pendingLabel; second click commits the full label.
export function beginPlaceArea(state, notify) {
  state.pendingLabel = { kind: 'area' };
  state.activeLabelIdx = -1;
  notify();
}

// Cancel any in-progress placement (Escape, mode change, etc.).
export function cancelPendingLabel(state, notify) {
  if (!state.pendingLabel) return;
  state.pendingLabel = null;
  notify();
}
